// CONTROLES DE VISUALIZACIÓN DEL RADAR INSTITUCIONAL (V2.1).
// Cantidad = HABITACIONES VISIBLES (no columnas). Orden y densidad son presentación pura: no
// modifican habitación, cama, residente, asignaciones, permisos ni responsabilidades.
// «Mi vista» es una selección PERSONAL y editable con controles táctiles simples (casillas),
// nunca arrastrar como único mecanismo.
// V2.1 · en pantallas estrechas los filtros secundarios viven dentro de «Filtros / Vista»
// (cerrado al abrir) y la búsqueda ocupa su propia fila completa: ninguna opción desaparece.
import { useState } from "react";
import { Search, Check, SlidersHorizontal, X, ChevronDown, ChevronUp } from "lucide-react";

const CANTIDADES = [2, 4, 8, 16, "todas"];
const ORDENES = [{ v: "numero", t: "Número" }, { v: "estado", t: "Estado" }];
const DENSIDADES = [{ v: "normal", t: "Normal" }, { v: "compacta", t: "Compacta" }];

function Grupo({ label, children, testId }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="hidden shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-400 lg:inline">{label}</span>
      <div data-testid={testId} className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100 p-0.5">{children}</div>
    </div>
  );
}

function Opcion({ activo, onClick, children, testId, title }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={activo} title={title}
      className={`gv2-compact min-h-9 rounded-lg px-2.5 text-[13px] font-bold transition-colors ${activo ? "bg-white text-[#012472] shadow-sm" : "text-slate-500 hover:text-[#012472]"}`}>
      {children}
    </button>
  );
}

export default function ControlesVista({
  prefs, onPref, q, onQ, habitaciones, onAlternar, editando, onEditar,
  visibles, totalHabitaciones, agrupacionSinUbicacion, persistente,
}) {
  const miVista = prefs.miVista || [];
  // VIS · el panel entra CERRADO en escritorio y en móvil. Colapsar sólo oculta los controles:
  // no cambia la vista elegida, no reinicia filtros y no altera qué habitaciones aparecen.
  const [abierto, setAbierto] = useState(false);
  const filtrosActivos = [
    q.trim() ? "búsqueda" : null,
    String(prefs.cantidad) !== "8" ? "habitaciones por página" : null,
    prefs.orden !== "numero" ? "orden" : null,
    prefs.densidad !== "normal" ? "densidad" : null,
  ].filter(Boolean);
  const limpiarFiltros = () => {
    onQ("");
    onPref({ cantidad: 8, orden: "numero", densidad: "normal" });
  };
  return (
    <div data-testid="radar-inst-controles" className="gv2-panel rounded-2xl">
      <div className="flex flex-wrap items-center gap-2 p-2 sm:px-3">
        <div className="flex min-w-0 flex-1 basis-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 sm:basis-0">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input data-testid="radar-inst-busqueda" value={q} onChange={(e) => onQ(e.target.value)}
            placeholder="Buscar residente o habitación"
            aria-label="Buscar residente o habitación"
            className="w-full min-w-0 border-0 bg-transparent py-2 text-sm text-slate-700 outline-none" />
          {q && (
            <button type="button" data-testid="radar-inst-busqueda-limpiar" onClick={() => onQ("")}
              aria-label="Limpiar la búsqueda"
              className="gv2-compact shrink-0 rounded text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button type="button" data-testid="radar-inst-filtros-toggle" onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto} aria-controls="radar-inst-filtros"
          title="Vista, cantidad de habitaciones, orden y densidad"
          className="gv2-chip-btn inline-flex shrink-0 items-center">
          <SlidersHorizontal className="h-4 w-4" /> Vista y filtros
          {abierto ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {!abierto && (
          <>
            <span data-testid="radar-inst-vista-actual"
              title="Conjunto de habitaciones que estás viendo ahora. Colapsar los controles no lo cambia."
              className="gv2-chip inline-flex min-w-0 shrink items-center">
              <span className="truncate">
                {prefs.usarMiVista ? `Mi vista · ${miVista.length}` : "Todo el centro"}
              </span>
            </span>
            {filtrosActivos.length > 0 && (
              <>
                <span data-testid="radar-inst-filtros-activos"
                  title={`Filtros aplicados: ${filtrosActivos.join(", ")}.`}
                  className="gv2-chip gv2-chip-action inline-flex min-w-0 shrink items-center">
                  <span className="truncate">{filtrosActivos.length} filtro{filtrosActivos.length === 1 ? "" : "s"} activo{filtrosActivos.length === 1 ? "" : "s"}</span>
                </span>
                <button type="button" data-testid="radar-inst-filtros-limpiar" onClick={limpiarFiltros}
                  className="gv2-chip-btn gv2-compact inline-flex shrink-0 items-center rounded-full">
                  <X className="h-3.5 w-3.5" /> Limpiar filtros
                </button>
              </>
            )}
          </>
        )}
      </div>

      <div id="radar-inst-filtros"
        className={`${abierto ? "flex" : "hidden"} flex-wrap items-center gap-x-4 gap-y-2 px-2 pb-2 sm:px-3`}>
        <Grupo label="Vista" testId="radar-inst-vista">
          <Opcion activo={!prefs.usarMiVista} onClick={() => onPref({ usarMiVista: false })}
            testId="radar-inst-vista-todo" title="Todas las habitaciones del centro">Todo el centro</Opcion>
          <Opcion activo={prefs.usarMiVista} onClick={() => onPref({ usarMiVista: true })}
            testId="radar-inst-vista-mia"
            title={miVista.length ? `${miVista.length} habitaciones elegidas por ti` : "Aún no has elegido habitaciones"}>
            Mi vista · {miVista.length}
          </Opcion>
        </Grupo>

        <Grupo label="Habitaciones" testId="radar-inst-cantidad">
          {CANTIDADES.map((c) => (
            <Opcion key={String(c)} activo={String(prefs.cantidad) === String(c)}
              onClick={() => onPref({ cantidad: c })} testId={`radar-inst-cantidad-${c}`}
              title={c === "todas" ? "Ver todas las habitaciones del conjunto elegido" : `Ver ${c} habitaciones por página`}>
              {c === "todas" ? "Todas" : c}
            </Opcion>
          ))}
        </Grupo>

        <Grupo label="Orden" testId="radar-inst-orden">
          {ORDENES.map((o) => (
            <Opcion key={o.v} activo={prefs.orden === o.v} onClick={() => onPref({ orden: o.v })}
              testId={`radar-inst-orden-${o.v}`}
              title={o.v === "numero" ? "Orden espacial por número de habitación (estable)" : "Primero las habitaciones con casos de mayor prioridad"}>
              {o.t}
            </Opcion>
          ))}
        </Grupo>

        <Grupo label="Densidad" testId="radar-inst-densidad">
          {DENSIDADES.map((d) => (
            <Opcion key={d.v} activo={prefs.densidad === d.v} onClick={() => onPref({ densidad: d.v })}
              testId={`radar-inst-densidad-${d.v}`}>{d.t}</Opcion>
          ))}
        </Grupo>

        <button type="button" data-testid="radar-inst-editar-vista" onClick={onEditar}
          className="gv2-chip-btn inline-flex items-center">
          {editando ? <X className="h-3.5 w-3.5" /> : <SlidersHorizontal className="h-3.5 w-3.5" />}
          {editando ? "Cerrar selección" : "Editar mi vista"}
        </button>
      </div>

      <p data-testid="radar-inst-conteo-habitaciones"
        className="px-2.5 pb-2 text-[11px] text-slate-400 sm:px-3">
        {visibles} de {totalHabitaciones} {totalHabitaciones === 1 ? "habitación" : "habitaciones"} en pantalla
        {agrupacionSinUbicacion ? " · más la agrupación de residentes sin ubicación confirmada" : ""}
        {!persistente && " · tus preferencias no se pueden guardar en este navegador (el Radar sigue funcionando)"}
      </p>

      {editando && (
        <div data-testid="radar-inst-selector-vista" className="border-t border-slate-100 bg-slate-50/70 px-2.5 py-3 sm:px-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Elige tus habitaciones · toca para añadir o quitar
          </p>
          <div className="flex flex-wrap gap-1.5">
            {habitaciones.filter((h) => h.zona_id).map((h) => {
              const elegida = miVista.includes(h.zona_id);
              return (
                <button key={h.zona_id} type="button" data-testid={`radar-inst-mivista-${h.zona_id}`}
                  onClick={() => onAlternar(h.zona_id)} aria-pressed={elegida}
                  className={`inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-bold transition-colors ${elegida ? "border-[#01CBE3] bg-[#01CBE3]/15 text-[#0891A6]" : "border-slate-200 bg-white text-slate-500 hover:border-[#01CBE3]"}`}>
                  {elegida && <Check className="h-3.5 w-3.5" />}{h.nombre}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
